import React, { FC, useEffect, useState } from 'react';
import { Backdrop, Box, InputAdornment, InputBase, Modal } from '@mui/material';
import './index.scss';
import { animated, useSpring } from 'react-spring';
import { JewelFarmBaseContext, JewelLendPool } from 'z/types';
import { convertBigNumberValueToLocalString, convertEsdtToWei, convertWeiToEsdt, ERROR_CONNECT_YOUR_WALLET, ERROR_EXCEED_MAX_VALUE, ERROR_VALUE_CANNOT_BE_NEGATIVE, getTokenDecimal, getTokenImage, getTokenTicker, MAX_PERCENTAGE, parseBigNumber, sleep, toastError } from 'z/utils';
import BigNumber from 'bignumber.js';
import { useGetAccount, useGetNetworkConfig } from '@multiversx/sdk-dapp/hooks';
import { sendJewelLend, sendJewelWithdraw } from 'z/elrond/jewel-lend';
import { isMobile } from 'react-device-detect';
import { queryGetEquivalent } from 'z/elrond';

const fadeBoxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: 300,
    // bgcolor: '#2b3943',
    boxShadow: 24,
    px: 3,
    py: 4,
    borderRadius: '10px',
    color: 'white',
    background: 'linear-gradient(180deg, #2b3943, #1a242b)',
};

interface FadeProps {
    children?: React.ReactElement;
    in: boolean;
    onEnter?: () => void;
    onExited?: () => void;
}

const Fade = React.forwardRef<HTMLDivElement, FadeProps>(function Fade(props, ref) {
    const { in: open, children, onEnter, onExited, ...other } = props;
    const style = useSpring({
        from: { opacity: 0 },
        to: { opacity: open ? 1 : 0 },
        onStart: () => {
            if (open && onEnter) {
                onEnter();
            }
        },
        onRest: () => {
            if (!open && onExited) {
                onExited();
            }
        },
    });

    return (
        <animated.div ref={ref} style={style} {...other}>
            {children}
        </animated.div>
    );
});

interface lendPoolProps {
    pool: JewelLendPool,
    farmCommonSettings: JewelFarmBaseContext,
}

export const LendPool: FC<lendPoolProps> = ({
    pool,
    farmCommonSettings
}) => {
    const {
        network: { apiAddress },
        chainID,
    } = useGetNetworkConfig();
    const { address } = useGetAccount();

    const [apy, setApy] = useState<string>('0');
    useEffect(() => {
        const deposit_token_id = pool.deposit_token_id;
        for (let i = 0; i < farmCommonSettings.farms.length; i++) {
            if (farmCommonSettings.farms[i].token_ids.includes(deposit_token_id)) {
                const _apy = farmCommonSettings.farms[i].apy * 0.175 / 0.375;
                setApy(_apy.toFixed(2));
                break;
            }
        }
    }, [farmCommonSettings, pool]);

    // const [depositTokenAmount, setDepositTokenAmount] = useState<string>('0');
    // useEffect(() => {
    //     (async () => {
    //         if (pool && address) {
    //             const _tokenAmount = await queryGetEquivalent(apiAddress, pool.ib_token_id, convertEsdtToWei(BigNumber(1), getTokenDecimal(pool.deposit_token_id)).toFixed());
    //             setDepositTokenAmount(_tokenAmount);
    //         }
    //     })();

    // }, [pool]);

    const [isDeposit, setIsDeposit] = useState<boolean>(true);
    const [showLendModal, setShowLendModal] = useState<boolean>(false);
    const [amount, setAmount] = useState<string>('0');
    const [amountError, setAmountError] = useState<string>('');
    const [isCalculating, setIsCalculating] = useState<boolean>(false);
    const [outAmount, setOutAmount] = useState<string>('');

    const getOutputAmount = async (inputAmount: string) => {
        setIsCalculating(true);
        let _outputAmount = '0';
        await sleep(500);
        if (isDeposit) {
            _outputAmount = await queryGetEquivalent(apiAddress, pool.deposit_token_id, convertEsdtToWei(inputAmount, getTokenDecimal(pool.deposit_token_id)).toFixed());
        } else {
            _outputAmount = await queryGetEquivalent(apiAddress, pool.ib_token_id, convertEsdtToWei(inputAmount, getTokenDecimal(pool.deposit_token_id)).toFixed());
        }
        setOutAmount(_outputAmount);
        setIsCalculating(false);
    };

    const handleMax = () => {
        let amount = '0';
        if (isDeposit) {
            amount = convertWeiToEsdt(pool.user_deposit_token_balance, getTokenDecimal(pool.deposit_token_id)).toFixed();
        } else {
            amount = convertWeiToEsdt(pool.user_ib_token_balance, getTokenDecimal(pool.deposit_token_id)).toFixed();
        }
        setAmount(amount);
        getOutputAmount(amount);
    };

    const checkAmount = (value: string) => {
        const bValue = parseBigNumber(value);
        let error = '';
        if (bValue == undefined) {
            error = 'Invalid number';
        } else if (bValue.comparedTo(0) < 0) {
            error = ERROR_VALUE_CANNOT_BE_NEGATIVE;
        } else {
            if (isDeposit && convertEsdtToWei(bValue, getTokenDecimal(pool.deposit_token_id)).comparedTo(pool.user_deposit_token_balance) > 0) {
                error = ERROR_EXCEED_MAX_VALUE;
            }
            if (!isDeposit && convertEsdtToWei(bValue, getTokenDecimal(pool.deposit_token_id)).comparedTo(pool.user_ib_token_balance) > 0) {
                error = ERROR_EXCEED_MAX_VALUE;
            }
        }

        return error;
    };

    const onChangeAmount = (value: string) => {
        const error = checkAmount(value);

        setAmountError(error);
        setAmount(value);
        getOutputAmount(value);
    };

    const onClickDeposit = async () => {
        if (!address) {
            toastError(ERROR_CONNECT_YOUR_WALLET);
            return;
        }
        setAmount('0');
        setOutAmount('0');
        setIsDeposit(true);
        setShowLendModal(true);
    };

    const onClickWithdraw = async () => {
        if (!address) {
            toastError(ERROR_CONNECT_YOUR_WALLET);
            return;
        }
        setAmount('0');
        setOutAmount('0');
        setIsDeposit(false);
        setShowLendModal(true);
    };

    const handleDeposit = async () => {
        if (!address) {
            toastError(ERROR_CONNECT_YOUR_WALLET);
            return;
        }

        if (amount == '' || BigNumber(amount).comparedTo(0) <= 0) {
            toastError('Invalid number');
            return;
        }

        const error = checkAmount(amount);
        if (error !== '') {
            toastError(error);
            return;
        }

        // deposit
        await sendJewelLend(chainID, address, pool.deposit_token_id, convertEsdtToWei(amount, getTokenDecimal(pool.deposit_token_id)).toFixed());

        setShowLendModal(false);
    };

    const handleWithdraw = async () => {
        if (!address) {
            toastError(ERROR_CONNECT_YOUR_WALLET);
            return;
        }

        if (amount == '' || BigNumber(amount).comparedTo(0) <= 0) {
            toastError('Invalid number');
            return;
        }

        const error = checkAmount(amount);
        if (error !== '') {
            toastError(error);
            return;
        }

        // withdraw
        await sendJewelWithdraw(chainID, address, pool.ib_token_id, convertEsdtToWei(amount, getTokenDecimal(pool.deposit_token_id)).toFixed());

        setShowLendModal(false);
    };

    return (
        <>
            {isMobile ? (
                <div className='row mt-4 lend-pool-container mobile-lend-pool-container'>
                    <div className='col-lg-2 col-md-2 col-sm-12 align-self-center d-flex'>
                        <div className='d-flex justify-content-center align-items-center'>
                            <img src={getTokenImage(pool.deposit_token_id)} className='egld-image mx-2' alt={pool.deposit_token_id}></img>
                            <div>
                                <div>{pool.deposit_token_id}</div>
                                {/* <div>1 ibUSDT = 1.0229 USDT</div> */}
                            </div>
                        </div>
                    </div>
                    <div className='col-lg-1 col-md-1 col-sm-12 align-self-center d-flex justify-content-between mt-3'>
                        <div>APY:</div>
                        <div className='avaliable-color'>{`${apy} %`}</div>
                    </div>
                    <div className='col-lg-2 col-md-2 col-sm-12 align-self-center d-flex justify-content-between mt-1'>
                        <div>Total Supply</div>
                        <div>{`${convertBigNumberValueToLocalString(convertWeiToEsdt(pool.deposit_token_amount, getTokenDecimal(pool.deposit_token_id)))} ${getTokenTicker(pool.deposit_token_id)}`}</div>
                    </div>
                    <div className='col-lg-2 col-md-2 col-sm-12 align-self-center d-flex justify-content-between mt-1'>
                        <div>Total Borrowed</div>
                        <div>{`${convertBigNumberValueToLocalString(convertWeiToEsdt(pool.loan_amount, getTokenDecimal(pool.deposit_token_id)))} ${getTokenTicker(pool.deposit_token_id)}`}</div>
                    </div>
                    <div className='col-lg-1 col-md-1 col-sm-12 align-self-center d-flex justify-content-between mt-1'>
                        <div>Utilization</div>
                        <div className='avaliable-color'>{`${convertBigNumberValueToLocalString(BigNumber(pool.loan_amount).multipliedBy(100).dividedBy(pool.deposit_token_amount))} %`}</div>
                    </div>
                    <div className='col-lg-2 col-md-2 col-sm-12 align-self-center d-flex justify-content-between mt-1'>
                        <div className='align-self-center'>Your Balance</div>
                        <div>
                            <div>{`${convertBigNumberValueToLocalString(convertWeiToEsdt(pool.user_ib_token_balance, getTokenDecimal(pool.deposit_token_id)))} ${getTokenTicker(pool.ib_token_id)}`}</div>
                            <div>{`${convertBigNumberValueToLocalString(convertWeiToEsdt(pool.user_deposit_token_balance, getTokenDecimal(pool.deposit_token_id)))} ${getTokenTicker(pool.deposit_token_id)}`}</div>
                        </div>
                    </div>
                    <div className='col-lg-2 col-md-2 col-sm-12 align-self-center mt-2'>
                        <div className='row'>
                            <div className='col-12 d-flex justify-content-center'>
                                <div className="eg-btn btn--primary2 capsule px-2 py-2" style={{ cursor: 'pointer', width: '100%' }} onClick={onClickDeposit}>
                                    Deposit
                                </div>
                            </div>
                            <div className='col-12 d-flex justify-content-center mt-2'>
                                <div className="eg-btn btn--primary2 capsule px-2 py-2" style={{ cursor: 'pointer', width: '100%' }} onClick={onClickWithdraw}>
                                    Withdraw
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className='row mt-4 lend-pool-container'>
                    <div className='col-lg-3 col-md-3 col-sm-12 align-self-center'>
                        <div className='d-flex align-items-center'>
                            <img src={getTokenImage(pool.deposit_token_id)} className='egld-image mx-2' alt={pool.deposit_token_id}></img>
                            <div>
                                <div>{pool.deposit_token_id}</div>
                                {/* <div>1 ibUSDT = 1.0229 USDT</div> */}
                            </div>
                        </div>
                    </div>
                    <div className='col-lg-1 col-md-1 col-sm-12 text-center align-self-center'>
                        <div className='avaliable-color'>{`${apy} %`}</div>
                    </div>
                    <div className='col-lg-3 col-md-3 col-sm-12 d-flex'>
                        <div className='col-lg-6 col-md-6 col-sm-12 text-center align-self-center'>
                            <div>{`${convertBigNumberValueToLocalString(convertWeiToEsdt(pool.deposit_token_amount, getTokenDecimal(pool.deposit_token_id)))} ${getTokenTicker(pool.deposit_token_id)}`}</div>
                        </div>
                        <div className='col-lg-6 col-md-6 col-sm-12 text-center align-self-center'>
                            <div>{`${convertBigNumberValueToLocalString(convertWeiToEsdt(pool.loan_amount, getTokenDecimal(pool.deposit_token_id)))} ${getTokenTicker(pool.deposit_token_id)}`}</div>
                        </div>
                    </div>
                    <div className='col-lg-1 col-md-1 col-sm-12 text-center align-self-center'>
                        <div className='avaliable-color'>{`${convertBigNumberValueToLocalString(BigNumber(pool.loan_amount).multipliedBy(100).dividedBy(pool.deposit_token_amount))} %`}</div>
                    </div>
                    <div className='col-lg-2 col-md-2 col-sm-12 text-end align-self-center'>
                        <div>{`${convertBigNumberValueToLocalString(convertWeiToEsdt(pool.user_ib_token_balance, getTokenDecimal(pool.deposit_token_id)))} ${getTokenTicker(pool.ib_token_id)}`}</div>
                        <div>{`Avail. ${convertBigNumberValueToLocalString(convertWeiToEsdt(pool.user_deposit_token_balance, getTokenDecimal(pool.deposit_token_id)))} ${getTokenTicker(pool.deposit_token_id)}`}</div>
                    </div>
                    <div className='col-lg-2 col-md-2 col-sm-12 align-self-center'>
                        <div className='row'>
                            <div className='col-12 d-flex justify-content-center'>
                                <div className="eg-btn btn--primary2 capsule px-2 py-2" style={{ cursor: 'pointer', width: '100%' }} onClick={onClickDeposit}>
                                    Deposit
                                </div>
                            </div>
                            <div className='col-12 d-flex justify-content-center mt-2'>
                                <div className="eg-btn btn--primary2 capsule px-2 py-2" style={{ cursor: 'pointer', width: '100%' }} onClick={onClickWithdraw}>
                                    Withdraw
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={showLendModal}
                onClose={() => setShowLendModal(false)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={showLendModal}>
                    <Box sx={fadeBoxStyle} className='lend-modal' style={{ width: 'auto' }}>
                        <div className='d-flex align-items-center'>
                            <div>{`${isDeposit ? 'Depositing' : 'Withdrawing'} ${getTokenTicker(pool.deposit_token_id)}`}</div>
                            <img src={getTokenImage(pool.deposit_token_id)} className='egld-image mx-2' alt={pool.deposit_token_id}></img>
                        </div>
                        <div className='d-flex mt-3'>
                            {`Available Balance: ${isDeposit ? `${convertBigNumberValueToLocalString(convertWeiToEsdt(pool.user_deposit_token_balance, getTokenDecimal(pool.deposit_token_id)))} ${getTokenTicker(pool.deposit_token_id)}` : `${convertBigNumberValueToLocalString(convertWeiToEsdt(pool.user_ib_token_balance, getTokenDecimal(pool.deposit_token_id)))} ${getTokenTicker(pool.ib_token_id)}`}`}
                        </div>
                        <div className='d-flex mt-1'>
                            <InputBase
                                sx={{
                                    flex: 1,
                                    border: '1px',
                                    borderStyle: 'solid',
                                    borderRadius: '5px',
                                    borderColor: 'gray',
                                    padding: '2px 5px',
                                }}
                                endAdornment={<InputAdornment position="end" sx={{ cursor: 'pointer' }} onClick={handleMax}>Max</InputAdornment>}
                                type='number'
                                placeholder='0.00'
                                value={amount}
                                onChange={(e: any) => onChangeAmount(e.target.value)}
                            />
                        </div>
                        <div className='d-flex justify-content-end text-danger mt-1' style={{ fontSize: '.8rem' }}>
                            {amountError}
                        </div>
                        <div className='d-flex mt-3'>
                            {`You will receive: ~ ${isCalculating ? '?' : convertBigNumberValueToLocalString(convertWeiToEsdt(outAmount, getTokenDecimal(pool.deposit_token_id)))} ${isDeposit ? getTokenTicker(pool.ib_token_id) : getTokenTicker(pool.deposit_token_id)}`}
                        </div>
                        {isDeposit && (
                            <div className='d-flex mt-2'>
                                {`The amount of ibTokens you'll receive is worth the same as the amount of tokens you deposited. There is no fee and you are not losing any value. Their exchange rate is not 1:1.`}
                            </div>
                        )}
                        <div className='d-flex justify-content-center mt-4'>
                            {isDeposit ? (
                                <div className="eg-btn btn--primary2 capsule px-4 py-2" style={{ cursor: 'pointer' }} onClick={handleDeposit}>
                                    Deposit
                                </div>
                            ) : (
                                <div className="eg-btn btn--primary2 capsule px-4 py-2" style={{ cursor: 'pointer' }} onClick={handleWithdraw}>
                                    Withdraw
                                </div>
                            )}
                        </div>
                    </Box>
                </Fade>
            </Modal>
        </>
    );
};
